import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import { CheckCircle2, AlertCircle, Info, X } from 'lucide-react';

type ToastKind = 'success' | 'error' | 'info';
interface Toast {
  id: number;
  message: string;
  kind: ToastKind;
}

interface ToastContextType {
  toast: (message: string, kind?: ToastKind) => void;
}

const Ctx = createContext<ToastContextType | undefined>(undefined);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const toast = useCallback((message: string, kind: ToastKind = 'info') => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, message, kind }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 3500);
  }, []);

  const dismiss = (id: number) => setToasts((t) => t.filter((x) => x.id !== id));

  return (
    <Ctx.Provider value={{ toast }}>
      {children}
      <div className="fixed bottom-4 right-4 z-[100] flex flex-col gap-2 max-w-sm">
        {toasts.map((t) => {
          const Icon = t.kind === 'success' ? CheckCircle2 : t.kind === 'error' ? AlertCircle : Info;
          const tone = t.kind === 'success' ? 'text-green-600' : t.kind === 'error' ? 'text-red-600' : 'text-blue-600';
          return (
            <div key={t.id} className="flex items-start gap-2 rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-lg">
              <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${tone}`} />
              <span className="flex-1 text-sm text-gray-800">{t.message}</span>
              <button onClick={() => dismiss(t.id)} className="text-gray-300 hover:text-gray-600" aria-label="Dismiss">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          );
        })}
      </div>
    </Ctx.Provider>
  );
}

export function useToast(): ToastContextType {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useToast must be used within a ToastProvider');
  return ctx;
}
